import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CLIENT_RELATION_CHAIN,
  createScenario,
  deleteScenario,
  fetchScenarioStats,
  listScenarios,
  SCENARIO_SIDES,
  type CoreScenario,
  type ScenarioSide,
} from '@/lib/coreScenarios';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ScenariosTab({ org }: Props) {
  const [scenarios, setScenarios] = useState<CoreScenario[]>([]);
  const [stats, setStats] = useState({ total: 0, business: 0, client: 0 });
  const [side, setSide] = useState<ScenarioSide>('business');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [form, setForm] = useState({ label: '', businessType: '', summary: '', coreFoundation: '', modules: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listScenarios(), fetchScenarioStats()]);
      setScenarios(list);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = scenarios.filter((s) => s.side === side);

  const onAdd = async () => {
    if (!form.label.trim()) { setError('Le libellé est requis.'); return; }
    setSaving(true);
    setError('');
    try {
      await createScenario({
        scenarioKey: `${side}_${Date.now()}`,
        side,
        label: form.label.trim(),
        businessType: form.businessType || null,
        summary: form.summary || null,
        coreFoundation: form.coreFoundation ? form.coreFoundation.split(',').map((s) => s.trim()).filter(Boolean) : [],
        modules: form.modules ? form.modules.split(',').map((s) => s.trim()).filter(Boolean) : [],
      });
      setForm({ label: '', businessType: '', summary: '', coreFoundation: '', modules: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: CoreScenario) => {
    setBusy(s.id);
    try { await deleteScenario(s.id); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Scénarios.</span> Le Core ne contient pas les modules :
        il les alimente. Côté entrepreneur, une activité compose ses modules au-dessus des fondations ; côté client,
        un utilisateur trouve, compare et réserve via l’IA — et le Core enregistre la relation.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-git-branch-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Scénarios</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-briefcase-4-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.business}</p>
          <p className="text-xs text-foreground-500">Côté entrepreneur</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-user-smile-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.client}</p>
          <p className="text-xs text-foreground-500">Côté client</p>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {SCENARIO_SIDES.map((s) => (
          <button key={s.key} onClick={() => setSide(s.key)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${side === s.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={s.icon}></i>{s.label}
          </button>
        ))}
      </div>

      {side === 'client' && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-2"><i className="ri-links-line text-accent-600"></i>Relation enregistrée par le Core</p>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            {CLIENT_RELATION_CHAIN.map((step, i, arr) => (
              <span key={step.key} className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full font-medium whitespace-nowrap flex items-center gap-1 ${i === 0 || i === 1 ? 'bg-background-100 text-foreground-700' : 'bg-accent-100 text-accent-900'}`}>
                  <i className={step.icon}></i>{step.label}
                </span>
                {i < arr.length - 1 && <i className="ri-arrow-right-line text-foreground-300"></i>}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-foreground-400 text-center py-8">Aucun scénario pour ce côté.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((s) => (
            <div key={s.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.side === 'business' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'}`}><i className={s.side === 'business' ? 'ri-briefcase-4-line' : 'ri-user-smile-line'}></i></span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{s.label}</p>
                  <p className="text-[11px] text-foreground-400 truncate">{s.business_type} · {s.summary}</p>
                </div>
                <button onClick={() => remove(s)} disabled={busy === s.id} className="ml-auto w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"><i className="ri-delete-bin-line text-sm"></i></button>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-[11px] font-semibold text-foreground-600 mb-1">Fondations Core</p>
                  <div className="flex flex-wrap gap-1.5">
                    {s.core_foundation.map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900">{f}</span>)}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-foreground-600 mb-1">Modules</p>
                  <div className="flex flex-wrap gap-1.5">
                    {s.modules.map((m) => <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{m}</span>)}
                  </div>
                </div>
                {s.example_questions.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-600 mb-1">Exemples de demandes</p>
                    <ul className="space-y-1">
                      {s.example_questions.map((qq) => (
                        <li key={qq} className="text-[11px] text-foreground-600 flex items-start gap-1.5"><i className="ri-chat-quote-line text-foreground-400 mt-0.5"></i>« {qq} »</li>
                      ))}
                    </ul>
                  </div>
                )}
                {s.tool_chain.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-600 mb-1">Enchaînement d’outils IA</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {s.tool_chain.map((t, i) => (
                        <span key={`${t}-${i}`} className="flex items-center gap-1.5">
                          <code className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-700">{t}()</code>
                          {i < s.tool_chain.length - 1 && <i className="ri-arrow-right-line text-foreground-300 text-[10px]"></i>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-add-line text-accent-600"></i>Ajouter un scénario ({side === 'business' ? 'entrepreneur' : 'client'})</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className={INPUT} placeholder="Libellé (ex : Salon de coiffure)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <input className={INPUT} placeholder="Type d’activité" value={form.businessType} onChange={(e) => setForm({ ...form, businessType: e.target.value })} />
        </div>
        <input className={`${INPUT} mt-3`} placeholder="Résumé" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <input className={INPUT} placeholder="Fondations Core (séparées par virgule)" value={form.coreFoundation} onChange={(e) => setForm({ ...form, coreFoundation: e.target.value })} />
          <input className={INPUT} placeholder="Modules (séparés par virgule)" value={form.modules} onChange={(e) => setForm({ ...form, modules: e.target.value })} />
        </div>
        <button onClick={onAdd} disabled={saving} className="mt-3 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Ajouter
        </button>
      </div>
    </div>
  );
}